import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import {
  reconcileCreditPaymentIntentKey,
  type AuthoritativeCreditPaymentState,
} from '@/lib/paymentSafety';

interface CreditPaymentTransactionRow {
  id: string;
  status: string | null;
}

interface CreditPurchaseStatusRow {
  payment_transaction_id: string;
  status: string | null;
}

interface CreditPaymentIntentReconciliationOptions {
  client: SupabaseClient<Database>;
  ownerType: 'organization' | 'candidate';
  ownerId: string | null | undefined;
}

export function summarizeCreditPaymentStates(
  transactions: CreditPaymentTransactionRow[],
  purchases: CreditPurchaseStatusRow[],
): AuthoritativeCreditPaymentState[] {
  const purchaseStatuses = new Map<string, string[]>();
  purchases.forEach((purchase) => {
    const statuses = purchaseStatuses.get(purchase.payment_transaction_id) ?? [];
    statuses.push(String(purchase.status ?? ''));
    purchaseStatuses.set(purchase.payment_transaction_id, statuses);
  });

  return transactions.map((transaction) => {
    const statuses = purchaseStatuses.get(transaction.id) ?? [];
    return {
      transactionId: transaction.id,
      paymentStatus: transaction.status,
      creditPurchaseStatus:
        statuses.length > 0 && statuses.every((status) => status === 'completed')
          ? 'completed'
          : statuses.length > 0
            ? 'incomplete'
            : null,
    };
  });
}

export function useCreditPaymentIntentReconciliation({
  client,
  ownerType,
  ownerId,
}: CreditPaymentIntentReconciliationOptions): void {
  const { data: authoritativeStates = [] } = useQuery<AuthoritativeCreditPaymentState[]>({
    queryKey: ['credit-payment-intent-reconciliation', ownerType, ownerId],
    queryFn: async () => {
      const { data: transactions, error: transactionError } = await client
        .from('payment_transactions')
        .select('id, status')
        .eq('owner_type', ownerType)
        .eq('owner_id', ownerId!)
        .eq('payment_type', 'credit_pack')
        .order('created_at', { ascending: false })
        .limit(20);
      if (transactionError) throw transactionError;

      const transactionRows = (transactions ?? []) as CreditPaymentTransactionRow[];
      if (transactionRows.length === 0) return [];

      const { data: purchases, error: purchaseError } = await client
        .from('credit_purchases')
        .select('payment_transaction_id, status')
        .in('payment_transaction_id', transactionRows.map((transaction) => transaction.id));
      if (purchaseError) throw purchaseError;

      return summarizeCreditPaymentStates(
        transactionRows,
        (purchases ?? []) as CreditPurchaseStatusRow[],
      );
    },
    enabled: !!ownerId,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    authoritativeStates.forEach(reconcileCreditPaymentIntentKey);
  }, [authoritativeStates]);
}
